<template>
  <QueryFilter @search="onSearch" />

  <!-- 待处理充值区(顶部,只在有待处理时显示) -->
  <div v-if="pendingList.length > 0" class="pending-section">
    <div class="pending-header">
      <div class="pending-title">
        <span class="dot">⌛</span>
        <span>{{ $t('query.pendingRecharges') }}</span>
        <span class="count">{{ pendingList.length }}</span>
      </div>
    </div>
    <div v-for="p in pendingList" :key="p.id" class="pending-item" @click="openPendingSummary(p)">
      <div class="pending-item__left">
        <div class="pending-item__amt">₡{{ p.amount }}</div>
        <div class="pending-item__method">{{ p.methodName || $t('query.rechargeAmount').replace(':', '') }}</div>
        <div class="pending-item__time">{{ $filters.formatDate(p.createdAt) }}</div>
      </div>
      <div class="pending-item__btn">{{ $t('recharge.viewInfo') }}</div>
    </div>
  </div>

  <!-- 已完成充值账单流水 -->
  <div class="list">
    <div v-for="item of list" :key="item.id" class="item">
      <div class="ic recharge"><van-icon name="cash-back-record" /></div>
      <div class="mid">
        <div class="title">{{ $t('query.rechargeAmount').replace(':', '') }}</div>
        <div class="time">{{ $filters.formatDate(item.createdAt) }}</div>
      </div>
      <div class="amt-recharge">+ ₡{{ item.amount }}</div>
    </div>
    <div v-if="list.length < total" class="load-more" @click="getMore">{{ $t('common.loadMore') }}</div>
    <div v-if="list.length === 0 && pendingList.length === 0" class="empty">
      <van-icon name="records" class="empty-icon" />
      <div>{{ $t('common.noData') }}</div>
    </div>
  </div>

  <!-- 复用的摘要弹窗:点待处理条目触发 -->
  <RechargeSummaryDialog
    v-model:show="showSummary"
    :info="summaryInfo"
    mode="pending"
    @confirm="showSummary = false"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { TopUpService } from '@/services';
import QueryFilter from './QueryFilter.vue';
import RechargeSummaryDialog from '@/components/RechargeSummaryDialog.vue';
import { storage } from '@/utils';
import { authMobile } from '@/constants';

const list = ref([]);
const total = ref(0);
const pageNumber = ref(1);
const isQuery = ref(false);
const filter = ref({ startTime: '', endTime: '' });

// 待处理充值(用于顶部区)
const pendingList = ref([]);
const showSummary = ref(false);
const summaryInfo = ref({});
const userMobile = storage.getItem(authMobile) || '';

const initResult = async () => {
  const { result } = await TopUpService.topUpPageListApi({
    pageNumber: pageNumber.value,
    startTime: filter.value.startTime,
    endTime: filter.value.endTime,
  });
  if (isQuery.value) {
    list.value = result.data || [];
    isQuery.value = false;
  } else if (result.data && result.data.length) {
    list.value = [...list.value, ...result.data];
  } else if (pageNumber.value === 1) {
    list.value = [];
  }
  total.value = result.total;
};

const initPending = async () => {
  try {
    const { result } = await TopUpService.pendingListApi();
    pendingList.value = Array.isArray(result) ? result : [];
  } catch (e) {
    pendingList.value = [];
  }
};

const openPendingSummary = (p) => {
  summaryInfo.value = {
    orderId: p.id,
    methodName: p.methodName || '',
    accountName: p.accountName || '',
    accountNo: p.accountNo || '',
    amount: p.amount,
    mobile: userMobile,
    isSinpe: (p.methodName || '').toLowerCase().includes('sinpe'),
  };
  showSummary.value = true;
};

const getMore = () => {
  pageNumber.value += 1;
  initResult();
};

const onSearch = (v) => {
  isQuery.value = true;
  pageNumber.value = 1;
  filter.value = v;
  initResult();
  initPending(); // 搜索时也刷新一下待处理
};

onMounted(() => {
  initResult();
  initPending();
});
</script>

<style lang="scss" scoped>
.list {
  padding: 0 16px;
}
.item {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 16px;
  padding: 18px 24px;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  .ic {
    width: 72px;
    height: 72px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 34px;
    margin-right: 20px;
    flex-shrink: 0;
    &.recharge {
      background: linear-gradient(135deg, #34d399, #10b981);
    }
  }
  .mid {
    flex: 1;
    .title {
      font-size: 28px;
      color: var(--text-primary);
      font-weight: 500;
      margin-bottom: 6px;
    }
    .time {
      font-size: 22px;
      color: var(--text-muted);
    }
  }
  .amt-recharge {
    font-size: 32px;
    font-weight: 700;
    color: #10b981;
  }
}
.load-more {
  text-align: center;
  color: var(--brand-primary);
  font-size: 26px;
  padding: 24px;
  cursor: pointer;
}
.empty {
  text-align: center;
  color: var(--text-muted);
  padding: 120px 0;
  font-size: 26px;
  .empty-icon {
    font-size: 100px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
}

// 待处理充值区(顶部橙色,视觉抢眼)
.pending-section {
  margin: 0 16px 24px 16px;
  background: linear-gradient(135deg, #fff8e1, #ffecb3);
  border: 2px solid #fbbf24;
  border-radius: 20px;
  padding: 20px;
}
.pending-header {
  margin-bottom: 12px;
}
.pending-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 26px;
  font-weight: 700;
  color: #92400e;
  .dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #f59e0b;
    color: #fff;
    border-radius: 50%;
    font-size: 22px;
  }
  .count {
    margin-left: auto;
    background: #f59e0b;
    color: #fff;
    padding: 4px 16px;
    border-radius: 999px;
    font-size: 22px;
    font-weight: 700;
  }
}
.pending-item {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 14px;
  padding: 16px 20px;
  margin-top: 10px;
  cursor: pointer;
  transition: transform 0.15s ease;
  &:active { transform: scale(0.98); }
  &__left { flex: 1; overflow: hidden; }
  &__amt {
    font-size: 32px;
    font-weight: 700;
    color: #764ba2;
    line-height: 1.2;
    margin-bottom: 4px;
  }
  &__method {
    font-size: 24px;
    color: #4b5563;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__time {
    font-size: 20px;
    color: var(--text-muted);
  }
  &__btn {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    padding: 14px 24px;
    border-radius: 999px;
    font-size: 24px;
    font-weight: 600;
    white-space: nowrap;
    margin-left: 16px;
  }
}
</style>
